// noinspection GraphQLUnresolvedReference

import { useMutation } from '@tanstack/react-query'

import type { GraphqlClientOptionsProps } from '../../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../../base/api/graphqlClient.ts'
import type { MutationOpts } from '../../../../base/types/reactQuery.ts'
import { gqlAst } from '../../../../utils/gql.ts'

const query = /* GraphQL */ gqlAst`
  mutation IMPRESORA_ELIMINAR($id: ID!) {
    impresoraEliminar(id: $id)
  }
`

export const MUTATION_KEY_IMPRESORA_ELIMINAR = ['impresora-eliminar'] as const

export interface ApiImpresoraEliminarParams {
  id: string
}

/**
 * Servicio para eliminar una impresora.
 * @param params Id de la impresora.
 * @param options Headers adicionales y señal de cancelación de la petición.
 * @returns `true` si el backend confirmó la eliminación.
 */
export const apiImpresoraEliminar = async (
  params: ApiImpresoraEliminarParams,
  options: GraphqlClientOptionsProps = {},
): Promise<boolean> => {
  try {
    const data = await graphqlClient.request<{ impresoraEliminar: boolean }>({
      document: query,
      variables: { ...params },
      requestHeaders: options.headers,
      signal: options.signal,
    })
    return data.impresoraEliminar
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Hook de mutación para eliminar una impresora.
 * Para refrescar al terminar:
 * `onSuccess: (_, { id }) => Promise.all([invalidateImpresoraListado(), invalidateImpresoraPorId(id)])`.
 * @param options Opciones de React Query (onSuccess, onError, ...).
 */
export const useImpresoraEliminar = (options?: MutationOpts<boolean, ApiImpresoraEliminarParams>) => {
  return useMutation({
    mutationKey: MUTATION_KEY_IMPRESORA_ELIMINAR,
    ...options,
    mutationFn: (params: ApiImpresoraEliminarParams) => apiImpresoraEliminar(params),
  })
}
